import React, { useCallback } from 'react';
import useRouter from '../utils/useRouter';
import { routes } from '../constants';
import services from '../services';
import { Box, Button, Stack, SvgIcon } from '@mui/material';
import { borderRadius, palette, spaces } from '../assets/styles/themes/tokens';
import { useTranslation } from 'react-i18next';
import invalidLinkSvg from '../assets/lernsets/invalid-public-link.svg';
import printer from '../assets/lernsets/icons/printer.svg';
import downloader from '../assets/lernsets/icons/download.svg';
import PublicContentView, { PublicContentData } from '../components/PublicContentView/PublicContentView';
import useScreenSize from '../utils/useScreenSize';
import { convertMarkdownToHtmlForPdf } from '../components/utils/contentConverter';
import DOMPurify from 'dompurify';
import { sanitizeFilename } from '../components/AIAssistant/shared/utils';
import { downloadHtmlAsPdf, printHtmlDocument } from '../utils/documentExport';
import PublicPageActionsMenu from '../components/PublicContentView/PublicPageActionsMenu';

interface HtmlPart {
  id: string;
  type: 'html';
  content: string;
}

const PublicGeneratedDocumentPage: React.FC = () => {
  const { match: { params: { accessToken } }, router } = useRouter();
  const { t } = useTranslation();
  const { isXsScreen, isSmScreen } = useScreenSize();
  const isSmallScreen = isXsScreen || isSmScreen;
  const a4ratio = 1.426;
  const imageMaxSideLength = 993;
  const defaultImageHeight = imageMaxSideLength;
  const defaultImageWidth = defaultImageHeight / a4ratio;

  const fetchDocument = async (token: string): Promise<PublicContentData<HtmlPart>> => {
    const publicDocument = await services.ai.getPublicDocument(token);
    const documentContentWithTitle = publicDocument.title 
      ? `#${publicDocument.title}#\n\n${publicDocument.content}` 
      : publicDocument.content;
    
    // Convert markdown to HTML with rendered KaTeX for both display and PDF export
    // This pre-rendered HTML is used directly by download/print handlers (no re-conversion needed)
    const rawHtml = convertMarkdownToHtmlForPdf(documentContentWithTitle);
    const htmlContent = DOMPurify.sanitize(rawHtml, {
      USE_PROFILES: { html: true, svg: true, mathMl: true },
      ADD_TAGS: ['link'],
      ADD_ATTR: ['style', 'class', 'rel', 'href', 'integrity', 'crossorigin'],
    });

    return {
      id: publicDocument.id,
      title: publicDocument.title,
      items: [{
        id: publicDocument.id,
        type: 'html',
        content: htmlContent,
      }],
    };
  };

  const handleDownload = async (data: PublicContentData<HtmlPart>) => {
    const sanitizedTitle = sanitizeFilename(data.title || '');
    // Content is already KaTeX HTML from fetchDocument, don't convert again
    await downloadHtmlAsPdf(data.items[0].content, sanitizedTitle, accessToken);
  };

  const handlePrint = async (data: PublicContentData<HtmlPart>) => {
    // Content is already KaTeX HTML from fetchDocument, don't convert again
    printHtmlDocument(data.title || '', data.items[0].content);
  };

  const handleError = (error: unknown) => {
    const errorObj = error as Error & { statusCode?: number };
    if (errorObj.statusCode === 403 || errorObj.message?.includes('expire')) {
      // Error will be handled by the generic component showing empty state
      return;
    } else {
      router.push(`/${routes.notFound.root}`);
    }
  };

  const renderItem = (item: HtmlPart) => (<Box
    key={item.id}
    data-testid={`generated-document-part-${item.id}`}
    sx={{ 
      position: 'relative',
      width: '100%',
      backgroundColor: palette.background.paperElevation2,
      borderRadius: isSmallScreen ? undefined : borderRadius.lg,
      paddingTop: spaces.md,
      paddingX: spaces.xl,
      '& > div': {
        padding: `0 ${spaces.md}`,
      },
    }}
    dangerouslySetInnerHTML={{ __html: item.content }}
  />
  );

  const renderTopBarButtons = useCallback(({
    isEmpty,
    onDownload,
    onPrint,
    isDownloadDisabled,
    isPrintDisabled,
  }: {
    isEmpty: boolean;
    onDownload: () => Promise<void>;
    onPrint: () => Promise<void>;
    isDownloadDisabled: boolean;
    isPrintDisabled: boolean;
    part?: HtmlPart;
  }) => {
    if (isEmpty) return undefined;

    const topBarButtons = (
      <Stack direction='row' spacing={spaces.xs}>
        <Button
          onClick={onPrint}
          variant='outlined'
          size='large'
          color='primary'
          disabled={isPrintDisabled}
          key='print-button'
          startIcon={
            <SvgIcon
              component={printer}
              sx={{ fill: isPrintDisabled ? palette.action.disabled : palette.primary.main }}
            />}
        >
          {t('Print')}
        </Button>
        <Button
          data-testid='public-page-download-button'
          onClick={onDownload}
          variant='contained'
          size='large'
          color='primary'
          disabled={isDownloadDisabled}
          key='download-button'
          startIcon={
            <SvgIcon 
              component={downloader} 
              sx={{ fill: isDownloadDisabled ? palette.action.disabled : palette.primary.contrastText }} 
            />}
        >
          {t('Download')}
        </Button>
      </Stack>
    );

    return isSmallScreen ? (
      <PublicPageActionsMenu
        onPrint={onPrint}
        onDownload={onDownload}
        isPrintDisabled={isPrintDisabled}
        isDownloadDisabled={isDownloadDisabled}
      />
    ) : topBarButtons;
  }, [isXsScreen, t]);

  return (
    <PublicContentView<HtmlPart>
      accessToken={accessToken}
      fetchData={fetchDocument}
      renderItem={renderItem}
      onPrint={handlePrint}
      onDownload={handleDownload}
      emptyStateIcon={invalidLinkSvg}
      emptyStateTitle={t('This page is no longer valid')}
      emptyStateMessage={t('It looks like the link has expired or the document has been deleted')}
      defaultImageWidth={defaultImageWidth}
      onError={handleError}
      renderTopBarButtons={renderTopBarButtons}
      noHeading={true}
    />
  );
};

export default PublicGeneratedDocumentPage;

